import { zodResolver } from '@hookform/resolvers/zod';
import {
  AssignIssueRequestDtoSeverityEnum,
  CreateActionTemplateDto,
  CreateIssueTemplateRequestDto,
  SLAViewDto,
} from '@issuemanager/api-client';
import {
  ControlledTextField,
  ExtendedControlledAutocomplete,
  GovmapGroupsSelector,
  UserSelector,
} from '@issuemanager/apps/client/components';
import {
  useAllQueries,
  useDateTimeFormat,
  useIssueTypeIssueSubtypeInfiniteScroll,
  useMonitorAddIssueTemplatesFormIssueSubtypesLoad,
  useMonitorAddIssueTemplatesFormLoad,
} from '@issuemanager/apps/client/hooks';
import { disablePast } from '@issuemanager/apps/client/utils';
import {
  useLocalizedIssueTypes,
  useSeverityLocalizer,
} from '@issuemanager/client/features/issues';
import { useSLAByIssueTypeIssueSubtypeAndSeverity } from '@issuemanager/client/features/slas';
import {
  ISSUE_TITLE_LENGTH_MAX,
  ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES,
  IssueType,
  UserQueryMode,
} from '@issuemanager/constants';
import { Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  IssueTemplateSchemaType,
  getIssueTemplateSchema,
} from './issue-template-form-schema';
import { useActionsForm } from './use-actions-form';

export interface ExtendedActionField extends CreateActionTemplateDto {
  customId: string;
}

export interface ExtendedCreateIssueTemplateDto
  extends CreateIssueTemplateRequestDto {
  actions: ExtendedActionField[];
}

type UseCreateUpdateIssueTemplateProps = {
  issueTypeId?: string;
  issueSubtypeId?: string | null;
  defaultValues?: ExtendedCreateIssueTemplateDto;
};

type LocalFields =
  | 'issueType'
  | 'issueSubtype'
  | 'title'
  | 'isAutomatic'
  | 'executionType'
  | 'severity'
  | 'managerId'
  | 'ownerId'
  | 'approverId'
  | 'dueDate'
  | 'govmapGroups';

function isDateEarlierThanSLA(
  SLAs: SLAViewDto[],
  severity: string,
  value: string,
): boolean {
  const selectedSla = SLAs.find((sla) => sla.severity === severity);

  if (!selectedSla) return true;

  return Number(value) <= selectedSla.maxDueDate;
}

export const useIssueTemplateForm = (
  props: UseCreateUpdateIssueTemplateProps,
) => {
  const { issueTypeId, issueSubtypeId } = props;
  const { t } = useTranslation(['issue', 'settings', 'action', 'common']);
  const { timezoneOffset } = useDateTimeFormat();
  const monitorAddIssueTemplateIssueSubtypesLoad =
    useMonitorAddIssueTemplatesFormIssueSubtypesLoad();
  const localizedIssueTypes = useLocalizedIssueTypes();
  const getSeverityLabel = useSeverityLocalizer();
  const monitorAddIssueTemplateFormLoad = useMonitorAddIssueTemplatesFormLoad();

  const [schema, setSchema] = useState<IssueTemplateSchemaType>(
    getIssueTemplateSchema({
      t,
      timezoneOffset,
      disablePast,
      SLAs: [],
      isDateEarlierThanSLA,
    }),
  );

  const form = useForm<ExtendedCreateIssueTemplateDto>({
    mode: 'all',
    resolver: zodResolver(schema) as Resolver<ExtendedCreateIssueTemplateDto>,
    defaultValues: props.defaultValues,
  });

  const actionsForm = useActionsForm({
    form,
    defaultValues: props.defaultValues!,
  });

  const [
    selectedIssueType,
    selectedSubtype,
    selectedSeverity,
    isAutomatic,
    managerId,
    ownerId,
    approverId,
  ] = form.watch([
    'issueTypeId',
    'issueSubtypeId',
    'severity',
    'isAutomatic',
    'managerId',
    'ownerId',
    'approverId',
  ]);

  const {
    issueTypeOptions,
    subtypesOptions,
    handleInputChange,
    isLoading: queryIsLoading,
    issueTypesQuery,
    issueSubtypesQuery,
  } = useIssueTypeIssueSubtypeInfiniteScroll({
    selectedIssueType,
    issueTypeQueryParams: {
      filterById: issueTypeId,
      filterByIssueTypeManager: true,
    },
    issueSubtypeQueryParams: {
      filterById: issueSubtypeId ?? undefined,
      filterByIssueTypeManager: true,
      handleError: true,
    },
  });

  const { matchingSLA, slasQuery } = useSLAByIssueTypeIssueSubtypeAndSeverity({
    issueType: selectedIssueType,
    issueSubtype: selectedSubtype ?? undefined,
    severity: selectedSeverity ?? undefined,
  });

  useEffect(() => {
    setSchema(
      getIssueTemplateSchema({
        t,
        timezoneOffset,
        disablePast,
        SLAs: matchingSLA ? [matchingSLA] : [],
        isDateEarlierThanSLA,
      }),
    );
  }, [slasQuery.dataUpdatedAt]);

  useAllQueries({
    queries: [issueSubtypesQuery],
    onStart: () => {
      monitorAddIssueTemplateIssueSubtypesLoad.start();
    },
    onSuccess: () => {
      monitorAddIssueTemplateIssueSubtypesLoad.stop();
    },
    onError: () => {
      monitorAddIssueTemplateIssueSubtypesLoad.error();
    },
  });
  useAllQueries({
    queries: [issueTypesQuery],
    onSuccess: () => {
      monitorAddIssueTemplateFormLoad.stop();
    },
    onError: () => {
      monitorAddIssueTemplateFormLoad.error();
    },
  });

  const labels = {
    title: t('settings:issueTemplates.form.labels.title'),
    isAutomatic: t('settings:issueTemplates.form.labels.isAutomatic'),
    issueTypeId: t('settings:issueTemplates.form.labels.issueType'),
    issueSubtypeId: t('settings:issueTemplates.form.labels.issueSubtype'),
    managerId: t('settings:issueTemplates.form.labels.manager'),
    ownerId: t('settings:issueTemplates.form.labels.owner'),
    approverId: t('settings:issueTemplates.form.labels.approver'),
    severity: t('settings:issueTemplates.form.labels.severity'),
    dueDate: t('settings:issueTemplates.form.labels.dueDate'),
    relatedOrgUnit: t('settings:issueTemplates.form.labels.relatedOrgUnit'),
  };

  const fields: Record<LocalFields, ReactNode> = {
    title: (
      <ControlledTextField
        name='title'
        label={labels.title}
        control={form.control}
        rules={{ required: true }}
        maxLength={ISSUE_TITLE_LENGTH_MAX}
      />
    ),
    isAutomatic: (
      <Controller
        name='isAutomatic'
        control={form.control}
        render={({ field: { value, onChange } }) => (
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => onChange(e.currentTarget.checked)}
                checked={Boolean(value)}
                data-testid='isAutomatic-checkbox'
              />
            }
            label={labels.isAutomatic}
            style={{ whiteSpace: 'nowrap' }}
          />
        )}
      />
    ),
    issueType: (
      <ControlledTextField
        name='issueTypeId'
        label={labels.issueTypeId}
        data-testid='categoryId'
        control={form.control}
        rules={{ required: true }}
        options={issueTypeOptions.map((issueType) => ({
          value: issueType.id,
          label:
            localizedIssueTypes[issueType.name as IssueType] ?? issueType.name,
        }))}
        onChange={() => {
          form.setValue('issueSubtypeId', undefined);
        }}
        placeholder={t('common:inputs.listBox.empty')}
      />
    ),
    issueSubtype: (
      <ExtendedControlledAutocomplete
        name='issueSubtypeId'
        label={labels.issueSubtypeId}
        data-testid='subcategoryId'
        control={form.control}
        // issue-type + null is a valid combination.
        // So every time we have issueTypeId we should disable the issue issue-subtype field as well
        disabled={Boolean(issueTypeId)}
        options={subtypesOptions.map((issueSubtype) => ({
          id: issueSubtype.id,
          label: issueSubtype.name,
        }))}
        loading={issueSubtypesQuery.isFetching}
        onTextChange={(value, reason) => {
          handleInputChange(value, 'issueSubtype', reason);
        }}
        placeholder={t('common:inputs.listBox.empty')}
      />
    ),
    managerId: (
      <Controller
        name='managerId'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <UserSelector
            value={value}
            initialValue={managerId}
            onChange={onChange}
            queryMode={UserQueryMode.ONLY_ISSUE_TYPE_MANAGERS}
            issueTypeId={selectedIssueType}
            issueSubtypeId={selectedSubtype ?? undefined}
            TextFieldProps={{
              required: isAutomatic,
              label: labels.managerId,
              'data-testid': 'managerId',
              error: !!error?.message,
              helperText: error?.message,
              inputProps: {
                'data-testid': 'managerId-input',
              },
            }}
          />
        )}
      />
    ),
    ownerId: (
      <Controller
        name='ownerId'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <UserSelector
            value={value}
            initialValue={ownerId}
            onChange={onChange}
            TextFieldProps={{
              required: isAutomatic,
              label: labels.ownerId,
              'data-testid': 'ownerId',
              error: !!error?.message,
              helperText: error?.message,
              inputProps: {
                'data-testid': 'ownerId-input',
              },
            }}
          />
        )}
      />
    ),
    approverId: (
      <Controller
        name='approverId'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <UserSelector
            value={value}
            initialValue={approverId}
            onChange={onChange}
            TextFieldProps={{
              required: isAutomatic,
              label: labels.approverId,
              'data-testid': 'approverId',
              error: !!error?.message,
              helperText: error?.message,
              inputProps: {
                'data-testid': 'approverId-input',
              },
            }}
          />
        )}
      />
    ),
    severity: (
      <ControlledTextField
        name='severity'
        label={labels.severity}
        control={form.control}
        rules={{ required: isAutomatic }}
        options={Object.entries(AssignIssueRequestDtoSeverityEnum).map(
          ([value, label]) => ({
            label: getSeverityLabel(label) ?? label,
            value,
          }),
        )}
      />
    ),
    dueDate: (
      <ControlledTextField
        name='dueDate'
        label={labels.dueDate}
        control={form.control}
        rules={{ required: isAutomatic }}
        onChange={() => {
          actionsForm.actions.forEach((action, index) => {
            if (action.dueDate) {
              form.trigger(`actions.${index}.dueDate`);
            }
          });
        }}
      />
    ),
    govmapGroups: (
      <Controller
        name='govmapGroups'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <GovmapGroupsSelector
            value={value}
            onChange={onChange}
            label={labels.relatedOrgUnit}
            error={!!error?.message}
            helperText={error?.message}
          />
        )}
      />
    ),
    executionType: (
      <Controller
        name='executionType'
        control={form.control}
        render={({ field: { value, onChange } }) => (
          <RadioGroup
            name='execution-type'
            value={value}
            onChange={(args) => {
              onChange(args);
              if (form.formState.isSubmitted) {
                form.trigger(`actions`);
              }
            }}
            sx={{
              gap: 2,
              '.MuiRadio-root': {
                padding: '3px',
                '&.Mui-disabled': {
                  color: '#676767',
                },
              },
              '&& .MuiFormControlLabel-label': {
                '&.Mui-disabled': {
                  color: '#676767',
                },
              },
              '&& .MuiFormControlLabel-root': {
                width: 'fit-content',
              },
            }}
          >
            {ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES.map((type) => (
              <FormControlLabel
                key={type}
                value={type}
                control={<Radio />}
                label={t(`issue:form.labels.executionType.${type}`)}
              />
            ))}
          </RadioGroup>
        )}
      />
    ),
  };

  return {
    form,
    isLoading: queryIsLoading,
    fields,
    cleanForm: form.reset,
    issueTypesQuery,
    issueSubtypesQuery,
    slasQuery,
  };
};
